
// Ownezzy Privacy Policy, written for the Digital Personal Data Protection Act, 2023 and the IT Act, 2000.
// Each section: { heading, text?: [paragraphs], list?: [items], after?: [paragraphs] }. Have changes reviewed by a lawyer before publishing.
// C: Ownezzy's contact (useOwnezzyContact in ./contact.js), shown where people are told how to reach Ownezzy
export const privacyFor = (C) => ({
  title: 'Privacy Policy',
  intro: [
    'This Privacy Policy explains how Ownezzy collects, uses, shares and protects personal data when you visit ownezzy.com, create or manage a store with us, or use the Ownezzy admin panel. It is written in line with the Digital Personal Data Protection Act, 2023 ("DPDP Act") and the Information Technology Act, 2000.',
    'By creating an account, starting a free trial or buying a paid plan, you agree to this Privacy Policy and to our Terms of Service.',
  ],
  sections: [
    {
      heading: '1. Who is responsible for your data',
      list: [
        'For Sellers (shop and business owners using Ownezzy) and visitors to ownezzy.com, Ownezzy decides how your data is used and is responsible for it.',
        'For shoppers buying from a Seller\'s store, that Seller is responsible for the shopper\'s personal data. Ownezzy stores and processes it only on the Seller\'s behalf to run the store. Shoppers should read the Privacy Policy on the store they buy from and contact that Seller about their data.',
      ],
    },
    {
      heading: '2. Data we collect',
      list: [
        'Account details: your name, WhatsApp/mobile number, email address and login ID. We never store your password in readable form.',
        'Business details: store name, business address, contact details, GST rate and shipping settings, and payment gateway settings (secret keys are stored encrypted).',
        'Store content: products, prices, images, banners, policies and other content you add.',
        'Orders and customers of your store: names, phone numbers, email and delivery addresses, and order details that your shoppers give when they order.',
        'Billing records: plans bought, amounts, dates and payment references. We do not store card numbers or UPI PINs.',
        'Technical data: IP address, browser and device type, and logs of important actions (such as sign-ins and setting changes) used for security.',
        'Messages: conversations you have with our support team, for example on WhatsApp.',
      ],
    },
    {
      heading: '3. Why we use it',
      list: [
        'to create and run your account and store, and to verify your email address;',
        'to send you your store details, sign-in help, billing reminders and important service messages;',
        'to process plan payments and keep billing and tax records;',
        'to give support when you ask for help;',
        'to keep the platform secure, prevent fraud and misuse, and investigate complaints;',
        'to improve Ownezzy using overall usage information;',
        'to meet legal obligations and respond to lawful requests from authorities.',
      ],
      after: ['We use your data with your consent (given when you sign up or start a plan) and for the legitimate uses allowed under the DPDP Act, such as meeting a legal obligation.'],
    },
    {
      heading: '4. Who we share it with',
      text: ['We do not sell your personal data. We share it only with:'],
      list: [
        'service providers who help us run Ownezzy, such as cloud hosting and storage, content delivery (CDN), website analytics (Google Analytics), WhatsApp messaging (Meta) and payment gateways, under contracts that require them to protect it;',
        'the Seller whose store a shopper orders from (for shoppers\' order details);',
        'government authorities or courts when Indian law requires it, or to protect the rights and safety of users and Ownezzy.',
      ],
    },
    {
      heading: '5. Where it is stored and how it is protected',
      text: [
        'Your data is stored on secure cloud servers used by Ownezzy and its service providers. We use reasonable security practices, including encrypted connections (HTTPS), encrypted storage of payment secrets, hashed passwords, access controls and activity logs.',
        'No system is completely secure. If a personal data breach happens, we will inform affected users and the Data Protection Board of India as the law requires.',
      ],
    },
    {
      heading: '6. How long we keep it',
      text: ['We keep personal data while your account is active and for as long as needed to provide the service, resolve disputes and meet legal, tax and accounting requirements. When it is no longer needed, we delete it or make it anonymous.'],
    },
    {
      heading: '7. Your rights',
      text: ['Under the DPDP Act you can:'],
      list: [
        'ask for a summary of the personal data we hold about you and how it is used;',
        'ask us to correct, complete, update or delete your personal data;',
        'withdraw your consent at any time (this may mean we can no longer run your store);',
        'nominate another person to use these rights for you in case of death or incapacity;',
        'raise a complaint with our Grievance Officer, and then with the Data Protection Board of India if you are not satisfied.',
      ],
    },
    {
      heading: '8. Cookies and analytics',
      text: [
        'Our sites use your browser\'s local storage to keep you signed in, remember your cart and remember settings. You can clear this storage from your browser at any time; you will then need to sign in again.',
        'On ownezzy.com we use Google Analytics to understand how visitors find and use our website, for example which pages are visited and how people arrive. Google Analytics sets cookies and collects information such as pages viewed, approximate location, device and browser. It does not give us your name or contact details. We have turned off sharing this data with Google for its other products, and we do not use advertising cookies. Google Analytics is not used on our sellers\' store websites.',
        'You can block or delete cookies in your browser settings, or use Google\'s opt-out add-on at https://tools.google.com/dlpage/gaoptout.',
      ],
    },
    {
      heading: '9. Children',
      text: ['Ownezzy is meant for adults running a business. We do not knowingly collect personal data from anyone under 18 without verifiable consent of a parent or guardian.'],
    },
    {
      heading: '10. Changes to this policy',
      text: ['We may update this Privacy Policy. The latest version is always on this page with its date. If a change is significant, we will tell you in the admin panel or on WhatsApp.'],
    },
    {
      heading: '11. Grievance Officer and contact',
      text: [
        `For any question, request or complaint about your personal data, contact our Grievance Officer at ${C.email} or on WhatsApp ${C.whatsapp}. We acknowledge complaints within 24 hours and aim to resolve them within 15 days.`,
      ],
    },
  ],
});
